import Text from 'app/Components/twComponents/Text'
import React from 'react'
import {_, LangLink, useI18n} from 'plugins/i18n'
import {COND_PAYINADVANCE, COND_PAYTHERE} from 'app/_server/database/constants'
import {
	CheckboxField,
	CheckboxInput,
	CountryInput,
	InputField,
	NumberInput,
	PhoneInput,
	TextInput,
} from 'app/Components/Inputs'
import {MoneyValue} from 'app/Components/Numbers'
import TwButton from 'app/Components/twComponents/Button'
import {useSelector} from 'react-redux'
import {getFormSyncErrors} from 'redux-form'
import {validators} from 'app/Components/Form'
import {LabelText} from 'app/Components/Inputs/Components/InputComponents'
import {useReservationFormState} from '../hooks'
import {calcExtraTypes, normalizeExtras} from '../helpers'
import {isDeepEmpty} from 'app/lib'
import {formId as reservationFormId} from '../Reservation'

export const NextStepButton = ({onClick}: {onClick: () => void}) => {
	const syncErrors = useSelector(state =>
		getFormSyncErrors(reservationFormId)(state as any)
	)
	const isValidForm = isDeepEmpty(syncErrors)
	return (
		<TwButton disabled={!isValidForm} onClick={onClick}>{_`NextStep`}</TwButton>
	)
}

export const ReservationSubmitButton = () => {
	const syncErrors = useSelector(state =>
		getFormSyncErrors(reservationFormId)(state as any)
	)
	const isValidForm = isDeepEmpty(syncErrors)
	return (
		<TwButton variant="primary" type="submit" disabled={!isValidForm}>
			{_`SubmitReservation`}
		</TwButton>
	)
}

export const GuestsInputs = ({
	contract,
}: {
	contract: Pick<Contract, 'adults' | 'kids' | 'babies' | 'house'>
}) => {
	const {adults, kids, house} = contract || {}
	const {capacity} = house || {}
	const isCapacityExceeded = adults + kids > capacity
	return (
		<div className="flex w-full flex-col gap-2">
			<div className="flex flex-col gap-8 sm:flex-row">
				<InputField
					Input={NumberInput}
					buttons
					name="contract.adults"
					label={_`adults`}
					min={1}
					column
					validate={[
						validators.required,
						validators.number,
						validators.maxCapacity,
					]}
				/>
				<InputField
					Input={NumberInput}
					label={_`kids4_15`}
					buttons
					name="contract.kids"
					min={0}
					column
					validate={[validators.number]}
				/>
				<InputField
					Input={NumberInput}
					buttons
					name="contract.babies"
					label={_`babies0_3`}
					min={0}
					column
					validate={[validators.number]}
				/>
			</div>
			<span className="text-danger">
				{isCapacityExceeded ? _`capacityExceeded` : undefined}
			</span>
		</div>
	)
}

export const PetsInputs = ({
	contract,
}: {
	contract: Pick<Contract, 'pets' | 'house'>
}) => {
	const {pets, house} = contract || {}
	const {petsAllowed, petsMax} = house || {}
	return (
		<div className="flex w-full flex-col gap-4">
			{petsAllowed && petsMax ? (
				<>
					<div className="flex w-full flex-col items-start">
						<Text HtmlTag="h2" variant="heading2XL">
							{_`a$AreYouBringingPets`}
						</Text>
					</div>
					<div className="flex gap-15">
						<InputField
							buttons
							Input={NumberInput}
							name="contract.pets"
							label={_`pets`}
							min={0}
							short
							column
							validate={[validators.number]}
						/>
						{pets ? (
							<InputField
								name="contract.petsNote"
								label={_`petsNote`}
								placeholder={_`description`}
								Input={TextInput}
								column
							/>
						) : null}
					</div>
				</>
			) : (
				<Text>{_`petsAreNotAllowed`}</Text>
			)}
		</div>
	)
}

export const GuestsAndTaxPanel = () => {
	const {formValue} = useReservationFormState()
	const {contract} = formValue || {}
	const {house, prices} = contract || {}
	const {capacity} = house
	const {touristTax} = prices || {}

	return (
		<>
			<Text>
				{_`MaximumCapacity`}: {capacity} ({_`adults and kids`})
			</Text>
			<div className="flex w-full flex-col items-start gap-8">
				<GuestsInputs contract={contract} />
				<PetsInputs contract={contract} />
				{touristTax ? (
					<div className="flex w-full flex-col gap-4">
						<Text>
							{_`TouristTaxEstimate`}: <MoneyValue value={touristTax} />
						</Text>
					</div>
				) : null}
			</div>
		</>
	)
}

const ExtraRow = ({extra}: {extra: Extra}) => {
	const {optionId, price, per, pet, required, max} = extra
	const {isRealPer, isBool} = calcExtraTypes(extra)

	const LeftColumn =
		// countable, and not a pet (which is counted automatically)
		!isBool && !pet ? (
			<InputField
				Input={NumberInput}
				key={optionId}
				name={`selectableExtrasValues.${optionId}`}
				label={_`${optionId}`}
				inline
				right
				short
				min={0}
				max={max}
				disabled={required || pet}
				onBlur={e => e?.preventDefault()}
			/>
		) : (
			<InputField
				key={optionId}
				Input={CheckboxInput}
				name={`selectableExtrasValues.${optionId}`}
				label={_`${optionId}`}
				disabled={required || pet}
			/>
		)

	const RightColumn = price ? (
		<Text variant="small">
			<MoneyValue value={price} />
			{isRealPer ? ', ' : ''}
			{isRealPer ? (pet ? _(`a$per:PET${per}`) : _(`a$per:${per}`)) : ''}
		</Text>
	) : null

	return (
		<div className="border-silver flex w-full justify-between border-b py-2">
			{LeftColumn}
			{RightColumn}
		</div>
	)
}

/** This component renders a list of extras that can be selected by the user. */
export const ExtrasInputs = () => {
	const {formValue} = useReservationFormState()
	const {contract} = formValue || {}
	const {house} = contract || {}
	const {extras: houseExtras} = house || {}

	const extrasToDisplay = normalizeExtras(houseExtras).filter(
		e =>
			(e.condition === COND_PAYINADVANCE && (!!e.pet || !e.required)) ||
			(e.condition === COND_PAYTHERE && !e.required)
	)

	return (
		<>
			<Text>{_`AdditionalOptionsInfo`}</Text>
			{extrasToDisplay?.length ? (
				<div className="flex w-full flex-col items-start">
					{extrasToDisplay?.map(extra => (
						<ExtraRow extra={extra} key={extra.optionId} />
					))}
				</div>
			) : (
				<Text>{_`NoOptionsToSelect`}</Text>
			)}
		</>
	)
}

export const InsuranceInputs = ({
	contract,
}: {
	contract: Pick<Contract, 'adults' | 'kids' | 'babies'>
}) => {
	const {getText} = useI18n()
	const {adults, kids, babies} = contract || {}
	const guestsTotal = adults + kids + babies

	const headerCells = (
		<>
			<Text> </Text>
			<Text className="font-bold">{_`firstName`}</Text>
			<Text className="font-bold">{_`lastName`}</Text>
			<Text className="font-bold">{_`birthYear`}</Text>
		</>
	)

	const guestInputArray = Array.from({length: guestsTotal}).map((_0, i) => (
		<>
			<Text className="font-bold text-nowrap">
				{getText(`Guest`)} {i + 1}
			</Text>
			<InputField
				Input={TextInput}
				name={`contract.insuranceGuests[${i}].firstName`}
				placeholder={getText('firstName')}
				noLabel
				column
				required
			/>
			<InputField
				Input={TextInput}
				name={`contract.insuranceGuests[${i}].lastName`}
				placeholder={getText('lastName')}
				noLabel
				column
				required
			/>
			<InputField
				Input={NumberInput}
				name={`contract.insuranceGuests[${i}].birthYear`}
				placeholder={getText('birthYear')}
				max={new Date().getFullYear()}
				noLabel
				column
				short
			/>
		</>
	))

	return (
		<div className="flex w-full flex-col gap-4">
			<Text variant="headingL">{_`GuestsForInsuranceLabel`}</Text>
			<div className="hidden w-full grid-cols-[min-content_1fr_1fr_1fr] gap-4 sm:grid">
				{headerCells}
				{guestInputArray}
			</div>
			<div className="flex w-full flex-col gap-4 sm:hidden">
				{guestInputArray.map((_0, i) => (
					<>{guestInputArray[i]}</>
				))}
			</div>
		</div>
	)
}

export const InsuranceCancellationPanel = () => {
	const {formValue} = useReservationFormState()
	const {contract} = formValue || {}
	const {prices, hasInsurance} = contract || {}
	const {adults, kids, babies} = contract || {}
	const guestsTotal = adults + kids + babies

	return (
		<>
			<Text>{_`CancellationInsuranceInfo`}</Text>
			<div>
				<Text variant="headingL">{_`CancellationInsuranceList`}:</Text>
				<ul className="list-disc pl-6">{_`CancellationInsuranceListPoints`}</ul>
			</div>
			<LangLink to="/xx/insurance" target="_blank">
				<Text className="underline underline-offset-2">
					{_`readConditions`}
				</Text>
			</LangLink>
			<div className="border-silver flex w-full flex-col gap-4 rounded-lg border border-solid p-4 md:p-8">
				<div className="flex w-full flex-col items-center justify-between md:flex-row">
					<InputField
						Input={CheckboxInput}
						name="contract.hasInsurance"
						label={_`resInsurance`}
					/>
					<div className="flex flex-col gap-0.5 text-right">
						<Text variant="headingL">
							<MoneyValue value={prices?.insurance / guestsTotal} />{' '}
							{_`per:PERSON`}
						</Text>
						<Text variant="small">
							({prices.insuranceRate * 100}% {_`of the rental price`})
						</Text>
					</div>
				</div>
				{hasInsurance && <InsuranceInputs contract={contract} />}
			</div>
		</>
	)
}

export const ContactInputs = () => {
	return (
		<div className="flex w-full max-w-120 flex-col items-start gap-4">
			<Text HtmlTag="h3" variant="headingXL">{_`ContactLabel`}</Text>
			<div className="flex w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-4">
					<Text className="flex w-full flex-col items-start gap-2">
						<Text variant="small">{_`email`}*</Text>
						<Text variant="xsmall">{_`emailInfo`}</Text>
					</Text>
					<InputField
						Input={TextInput}
						name="client.email"
						placeholder={_`email`}
						noLabel
						column
						required
					/>
				</div>
				<div className="flex w-full flex-col gap-4 md:flex-row">
					<InputField
						Input={TextInput}
						name="client.name.first"
						placeholder={_`firstName`}
						label={_`firstName`}
						column
						required
					/>
					<InputField
						Input={TextInput}
						name="client.name.last"
						placeholder={_`lastName`}
						label={_`lastName`}
						column
						required
					/>
				</div>
				<InputField
					Input={PhoneInput}
					defaultCountry="BE"
					name="client.tel"
					label={_`phoneNumber`}
					column
					required
				/>
			</div>
		</div>
	)
}

export const AddressInputs = () => {
	return (
		<div className="flex w-full flex-col items-start gap-4">
			<Text HtmlTag="h3" variant="headingXL">{_`AddressLabel`}</Text>
			<div className="flex w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-4 md:flex-row">
					<InputField
						Input={TextInput}
						name="client.address.street"
						placeholder={_`street`}
						label={_`street`}
						autoComplete="address-line1"
						column
						required
					/>
				</div>
				<div className="flex w-full flex-col gap-4 md:flex-row">
					<InputField
						Input={TextInput}
						name="client.address.pcode"
						placeholder={_`pcode`}
						label={_`pcode`}
						autoComplete="postal-code"
						column
						required
					/>
					<InputField
						Input={TextInput}
						name="client.address.city"
						label={_('city')}
						placeholder={_`city`}
						autoComplete="address-level2"
						column
						required
					/>
					<InputField
						Input={CountryInput}
						name="client.address.countryId"
						placeholder={_`countryId`}
						autoComplete="address-country"
						label={_`countryId`}
						column
						required
					/>
				</div>
			</div>
		</div>
	)
}

export const ConsentsInputs = () => {
	return (
		<div className="flex w-full flex-col gap-4">
			<CheckboxField
				name="client.conditionsAccept"
				required
				label={<LabelText>{_('acceptResConditions')}</LabelText>}
			/>
			<CheckboxField
				name="client.newsletterAccept"
				label={<LabelText>{_('acceptNewsletterSubscription')}</LabelText>}
			/>
		</div>
	)
}
